import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ShoppingCart, MapPin } from 'lucide-react';
import { userAPI } from '../services/apiService';

const PlaceOrder = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    Officename: '',
    Officename: '',
    mobileNumber: '',
    fulladdress: '',
    latitude: '',
    longitude: '',
    productType: '',
    speedRequired: ''
  });

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProductChange = (value) => {
    setFormData({ ...formData, productType: value });
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
          toast.success('Location captured successfully!');
        },
        (error) => {
          toast.error('Unable to get location. Please enter manually.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form
    if (!formData.customerName || !formData.customerEmail || !formData.mobileNumber || 
        !formData.fulladdress || !formData.productType) {
      toast.error('Please fill all required fields');
      setIsLoading(false);
      return;
    }

    try {
      const order = await userAPI.createOrder(formData);
      
      toast.success('Order placed successfully! Order ID: ' + order._id);
      
      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        OfficeName: '',
        mobileNumber: '',
        fulladdress: '',
        latitude: '',
        longitude: '',
        productType: '',
        speedRequired: ''
      });
      
      setIsLoading(false);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order. Please try again.');
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Place New Order</h1>
              <p className="text-gray-700 text-lg font-medium">Submit customer order details</p>
            </div>
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Order Form */}
        <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-red-600" />
              Customer Order Details
            </CardTitle>
            <CardDescription>Fill in the customer information to place an order</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Name */}
              <div className="space-y-2">
                <Label htmlFor="customerName" className="text-gray-700 font-semibold">
                  Customer Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="customerName"
                  name="customerName"
                  placeholder="Enter customer full name"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="h-12"
                />
              </div>

              {/* Email and Mobile */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="customerEmail" className="text-gray-700 font-semibold">
                    Customer Email <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="customerEmail"
                    name="customerEmail"
                    type="email"
                    placeholder="customer@example.com"
                    value={formData.customerEmail}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mobileNumber" className="text-gray-700 font-semibold">
                    Mobile Number <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="mobileNumber"
                    name="mobileNumber"
                    type="tel"
                    placeholder="+91 9876543210"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address" className="text-gray-700 font-semibold">
                  Address <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="Enter complete address with city, state, and pincode"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="resize-none"
                />
              </div>

              {/* Location */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-gray-700 font-semibold">
                    Location Coordinates (Optional)
                  </Label>
                  <Button
                    type="button"
                    onClick={handleGetLocation}
                    variant="outline"
                    className="border-2 border-blue-400 text-blue-600 hover:bg-blue-50"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Get Current Location
                  </Button>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="latitude" className="text-gray-700 font-semibold">Latitude</Label>
                    <Input
                      id="latitude"
                      name="latitude"
                      type="text"
                      placeholder="28.6139"
                      value={formData.latitude}
                      onChange={handleChange}
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="longitude" className="text-gray-700 font-semibold">Longitude</Label>
                    <Input
                      id="longitude"
                      name="longitude"
                      type="text"
                      placeholder="77.2090"
                      value={formData.longitude}
                      onChange={handleChange}
                      className="h-12"
                    />
                  </div>
                </div>
              </div>

              {/* Product Type */}
              <div className="space-y-2">
                <Label htmlFor="productType" className="text-gray-700 font-semibold">
                  Product Type <span className="text-red-600">*</span>
                </Label>
                <Select value={formData.productType} onValueChange={handleProductChange}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Internet Leaseline">Internet Leaseline</SelectItem>
                    <SelectItem value="Lease Line Internet">Lease Line Internet</SelectItem>
                    <SelectItem value="Enterprise Broadband">Enterprise Broadband</SelectItem>
                    <SelectItem value="CCTV Security Camera">CCTV Security Camera</SelectItem>
                    <SelectItem value="Internet Networking">Internet Networking</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Speed Required */}
              {(formData.productType === 'Internet Leaseline' || formData.productType === 'Lease Line Internet' || formData.productType === 'Enterprise Broadband') && (
                <div className="space-y-2">
                  <Label htmlFor="speedRequired" className="text-gray-700 font-semibold">
                    Speed Required
                  </Label>
                  <Input
                    id="speedRequired"
                    name="speedRequired"
                    placeholder="e.g., 100 Mbps, 200 Mbps, 1 Gbps"
                    value={formData.speedRequired}
                    onChange={handleChange}
                    className="h-12"
                  />
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 text-lg font-bold text-white border-2 border-yellow-400 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
              >
                {isLoading ? 'Submitting Order...' : 'Submit Order'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 border-3 border-blue-300 bg-blue-50/50 backdrop-blur-sm">
          <CardContent className="p-6">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Once you submit the order, it will be reviewed by the admin. 
              Upon approval, commission will be credited to your pending balance. 
              You can track all your orders in the Transactions section.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlaceOrder;
